import { getDefaultLimitRepresentation } from "common/utils/limitRepresentation";
import { CleaningLimitPolicyResidueType } from "./enums";
import { PolicyConfigUI } from "./types";
import { DEFAULT_UNITS } from "./utils/default-units";

export const LIMIT_ABSENT = "Absent";
export const EFFECTIVE_LOD_LOQ_MULTIPLIER = 0.99999;
export const ROOM_GRADE_ID = "roomGradeId";

export const defaultCleaningPolicy: PolicyConfigUI = {
  useDosage: false,
  useToxicity: false,
  useProductLimit: false,
  useLd50: false,
  useARL: false,
  arl: { value: 10, unit: DEFAULT_UNITS.L5 },
  residueLimit: { value: 10, unit: DEFAULT_UNITS.L3 },
  useResidueLimit: false,
  ignoreCombo: false,
  useGlobalRecoveryPercentage: false,
  globalRecoveryPercentage: 100,
  residueType: CleaningLimitPolicyResidueType.Chemical,
  useSAR: false,
  limitRepresentation: getDefaultLimitRepresentation(),
  useFirstAvailableCriteria: false,
  intermediateArl: 0,
  activesArl: { value: 10, unit: DEFAULT_UNITS.L5 },
  useSampleLimit: false,
  swabSampleLimit: 10,
  rinseSampleLimit: 10,
  useFixedSampleLimits: false,
  fixedSwabLimit: 10,
  fixedRinseLimit: 10,
  enabled: true
};

// Keys of this constant should be the same as svg we are adding for the shape
// SVG image will be loaded on the basis of this.
export const GEOMETRY_OPTIONS = {
  cylinder: {
    formula: "2*π*r*h+2*π*(r**2)",
    visualFormula: "2π * r * h + 2 * πr^2",
    label: "Cylindrical",
    inputs: {
      r: "Radius (r)",
      h: "Height (h)"
    }
  },
  circle: {
    formula: "π*r**2",
    visualFormula: "πr^2",
    label: "Circle",
    inputs: {
      r: "Radius (r)"
    }
  },
  circular_ring: {
    formula: "π*(r**2)-π*(R1**2)",
    visualFormula: "πr^2 - πR1^2",
    label: "Circular ring",
    inputs: {
      r: "Radius (r)",
      R1: "Inner radius (R1)"
    }
  },
  cube: {
    formula: "6*(l**2)",
    visualFormula: "6*L^2",
    label: "Cube",
    inputs: {
      l: "Length (L)"
    }
  },
  cone: {
    // Do not use any parameters name same as any mathematical operations.
    // As for Ex: We are using Math.sqrt here then any parameters shouldn't be among these letters
    formula: "π*R*(R+Math.sqrt(H**2+R**2))",
    visualFormula: "πR (R+ sqrt(H^2 + R^2))",
    label: "Cone",
    inputs: {
      R: "Radius (r)",
      H: "Height (h)"
    }
  },
  square: {
    formula: "l**2",
    visualFormula: "l^2",
    label: "Square",
    inputs: {
      l: "Length (L)"
    }
  },
  rectangle: {
    formula: "l*h",
    visualFormula: "l*h",
    label: "Rectangle",
    inputs: {
      l: "Length (l)",
      h: "Height (h)"
    }
  },
  triangle: {
    formula: "(1/2)*b*h",
    visualFormula: "1/2 * b * h",
    label: "Triangle",
    inputs: {
      b: "base (b)",
      h: "Height (h)"
    }
  },
  cuboid_lateral: {
    formula: "2 * (l + b) * h",
    visualFormula: "2 * (l + b) * h",
    label: "Cuboid Lateral",
    inputs: {
      b: "Base (b)",
      h: "Height (h)",
      l: "Length (l)"
    }
  },
  cylinder_lateral: {
    formula: "2 * π * r * h",
    visualFormula: "2π * r * h",
    label: "Cylinder lateral surface",
    inputs: {
      r: "Radius (r)",
      h: "Height (h)"
    }
  },
  ellipse: {
    formula: "π*R1*R2 ",
    visualFormula: "πR1*R2 ",
    label: "Ellipse",
    inputs: {
      R1: "Radius1 (R1)",
      R2: "Radius2 (R2)"
    }
  },
  frustrum_curved: {
    formula: "π * (R1+R2) * S",
    visualFormula: "π * (R1+R2) * S",
    label: "Frustum curved surface",
    inputs: {
      R1: "Radius1 (R1)",
      R2: "Radius2 (R2)",
      S: "Slant Height (s)"
    }
  },
  hemisphere: {
    formula: "3 * π * r**2 ",
    visualFormula: "3 * πr^2 ",
    label: "Hemisphere",
    inputs: {
      r: "Radius2 (r)"
    }
  },
  perforated_turret: {
    formula: "π * r**2 - π * R1**2 - n*(π*R2**2)",
    visualFormula: "πr^2 - πR1^2 - n*(πR2^2)",
    label: "Perforated turret",
    inputs: {
      r: "Outer Radius (r)",
      R1: "Inner Radius (R1)",
      R2: "Radius of holes (R2)",
      n: "Number of holes (n)"
    }
  },
  rectangular_prism: {
    formula: "2 * (B*H + H*L + L*B)",
    visualFormula: "2 (B*H + H*L + L*B)",
    label: "Rectangular prism",
    inputs: {
      B: "Base (B)",
      H: "Height (H)",
      L: "Length (L)"
    }
  },
  trapezium_prism_lateral: {
    formula: "(L1 + B1) * H1 + (L2 + B2) * H2",
    visualFormula: "(L1 + B1) * H1 + (L2 + B2) * H2",
    label: "Trapezium prism lateral surface",
    inputs: {
      B1: "Base (B1)",
      H1: "Height (H1)",
      L1: "Length (L1)",
      B2: "Base (B2)",
      H2: "Height (H2)",
      L2: "Length (L2)"
    }
  },
  trapezium: {
    formula: "1/2 * (L + B) * H",
    visualFormula: "1/2 * (L + B) * H",
    label: "Trapezium",
    inputs: {
      B: "Base (B)",
      H: "Height (H)",
      L: "Length (L)"
    }
  },
  sphere: {
    formula: "4*π*r**2",
    visualFormula: "4 * πr^2",
    label: "Sphere",
    inputs: {
      r: "Radius (r)"
    }
  },
  curved_surface_area_of_a_hemisphere: {
    formula: "2*π*r**2",
    visualFormula: "2 * πr^2",
    label: "Curved surface area of a hemisphere",
    inputs: {
      r: "Radius (r)"
    }
  },
  lateral_surface_area_of_a_frustum_of_a_cone: {
    formula: "π * (r1 + r2) * Math.sqrt((r1 - r2)**2 + H**2)",
    visualFormula: "π * (r1 + r2) * √((r1 - r2)^2 + H^2)",
    label: "Lateral surface area of a frustum of a cone",
    inputs: {
      r1: "Radius (r1)",
      r2: "Radius (r2)",
      H: "Height (H)"
    }
  }
};

export const LIMIT_MATRIX_COLUMNS = [
  {
    columnName: "prev_production_id",
    displayName: "Previous Production (A)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Previous Production (A)"
  },
  {
    columnName: "prev_product_name_a",
    displayName: "Previous Product Name (A)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Previous Product Name (A)"
  },
  {
    columnName: "prev_material_type_a",
    displayName: "Material Type (A)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "Material Type (A)"
  },
  {
    columnName: "prev_api_name_a",
    displayName: "Previous API (A)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Previous API"
  },
  {
    columnName: "pde_a",
    displayName: "ADE (A)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "ADE"
  },
  {
    columnName: "ld50_a",
    displayName: "LD50 (A)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 6,
    description: "LD50"
  },
  {
    columnName: "min_td_a",
    displayName: "mDD (A)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "MDD"
  },
  {
    columnName: "safety_factor",
    displayName: "Safety Factor",
    dataType: "string",
    isVisible: false,
    isSortable: true,
    isSearchable: false,
    displayOrder: 8,
    description: "Safety Factor"
  },
  {
    columnName: "next_production_id",
    displayName: "Next Production ID (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 9,
    description: "Next Production ID"
  },
  {
    columnName: "next_material_type_b",
    displayName: "Material Type (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 10,
    description: "Material Type (B)"
  },
  {
    columnName: "next_product_name_b",
    displayName: "Next Product Name (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 11,
    description: "Next Product Name"
  },
  {
    columnName: "ldd_b",
    displayName: "LDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 12,
    description: "LDD (B)"
  },
  {
    columnName: "mdd_b",
    displayName: "MDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 13,
    description: "MDD (B)"
  },
  {
    columnName: "next_unit_weight",
    displayName: "Unit Size (B)",
    dataType: "string",
    isVisible: false,
    isSortable: true,
    isSearchable: false,
    displayOrder: 14,
    description: "Unit Size (B)"
  },
  {
    columnName: "min_bs_b",
    displayName: "Batch Size (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 15,
    description: "Batch Size"
  },
  {
    columnName: "shared_surface_area",
    displayName: "SSA (A-B)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 16,
    description: "Shared Surface Area"
  },
  {
    columnName: "general_l3",
    displayName: "SAL-Default",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 17,
    description: "SAL Default"
  },
  {
    columnName: "dosage_l3",
    displayName: "SAL-Dosage",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 18,
    description: "SAL Dosage"
  },
  {
    columnName: "ld50_l3",
    displayName: "SAL-LD50",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 19,
    description: "SAL LD50"
  },
  {
    columnName: "toxicity_l3",
    displayName: "SAL-Toxicity",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 20,
    description: "SAL Dosage"
  },
  {
    columnName: "cap_l3",
    displayName: "SAL CAP",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 21,
    description: "SAL CAP"
  },
  {
    columnName: "matrix_alert_limit_l3",
    displayName: "SAL-Final",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 22,
    description: "SAL Dosage"
  },
  {
    columnName: "adj_alert_limit_l3",
    displayName: "SAL-Adjusted",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 23,
    description: "SAL Adjusted"
  }
];

export const PRODUCT_LIMIT_COLUMNS = [
  {
    columnName: "prev_production_id",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Production Id"
  },
  {
    columnName: "prev_product_name_a",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Product Name"
  },
  {
    columnName: "prev_material_type_a",
    displayName: "Material Type",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "Material Type"
  },
  {
    columnName: "prev_api_name_a",
    displayName: "API Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "API Name"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Least SAL"
  }
];

export const PRODUCT_EQUIPMENT_LIMIT_COLUMNS = [
  {
    columnName: "prev_production_id",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Production Id"
  },
  {
    columnName: "prev_product_name_a",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Product Name"
  },
  {
    columnName: "prev_api_name_a",
    displayName: "API Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "API Name"
  },
  {
    columnName: "active_contact",
    displayName: "Active Contact",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Active Contact"
  },
  {
    columnName: "material_contact",
    displayName: "Material Contact",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Material Contact"
  },
  {
    columnName: "equipment_id",
    displayName: "Equipment Id",
    dataType: "string",
    isVisible: false,
    isSortable: false,
    isSearchable: true,
    displayOrder: 6,
    description: "Equipment Id"
  },
  {
    columnName: "equipment_name",
    displayName: "Equipment Name",
    dataType: "string",
    isVisible: false,
    isSortable: false,
    isSearchable: true,
    displayOrder: 7,
    description: "Equipment Name"
  },
  {
    columnName: "equipment_entity_id",
    displayName: "Equipment Entity Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 8,
    description: "Equipment Entity Id"
  },
  {
    columnName: "equipment_entity_name",
    displayName: "Equipment Entity Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 9,
    description: "Equipment Entity Name"
  },
  {
    columnName: "min_alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 10,
    description: "Least SAL"
  }
];

export const MACO_COLUMNS = [
  {
    columnName: "prev_production_id",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Production Id"
  },
  {
    columnName: "prev_product_name_a",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Product Name"
  },
  {
    columnName: "prev_api_name_a",
    displayName: "API Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "API Name"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "Least SAL"
  },
  {
    columnName: "next_production_id",
    displayName: "Next Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Next Production Id"
  },
  {
    columnName: "next_product_name_b",
    displayName: "Next Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 6,
    description: "Next Product Name"
  },
  {
    columnName: "shared_surface_area",
    displayName: "SSA",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "SSA"
  },
  {
    columnName: "maco",
    displayName: "MACO (mg)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 8,
    description: "MACO (mg)"
  }
];

export const MAXCONC_COLUMNS = [
  {
    columnName: "prev_production_id",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Production Id"
  },
  {
    columnName: "prev_product_name_a",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Product Name"
  },
  {
    columnName: "prev_api_name_a",
    displayName: "API Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "API Name"
  },
  {
    columnName: "next_production_id",
    displayName: "Next Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Next Production Id"
  },
  {
    columnName: "next_product_name_b",
    displayName: "Next Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Next Product Name"
  },
  {
    columnName: "batch_size_mg",
    displayName: "Batch Size (mg)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 6,
    description: "Batch Size (mg)"
  },
  {
    columnName: "batch_size_gm",
    displayName: "Batch Size (gm)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "Batch Size (gm)"
  },
  {
    columnName: "batch_size_kg",
    displayName: "Batch Size (kg)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 8,
    description: "Batch Size (kg)"
  },
  {
    columnName: "shared_surface_area",
    displayName: "SSA (sqcm)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 9,
    description: "SSA (sqcm)"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL (mg/sqcm)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 10,
    description: "Least SAL (mg/sqcm)"
  },
  {
    columnName: "maco",
    displayName: "MACO (mg)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 11,
    description: "MACO (mg)"
  },
  {
    columnName: "maco_ppm",
    displayName: "MACO (ppm)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 12,
    description: "MACO (ppm)"
  },
  {
    columnName: "maxconc_ppm",
    displayName: "MAXCONC (ppm)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 13,
    description: "MAXCONC (ppm)"
  }
];

export const CLEANING_AGENT_LIMIT_MATRIX_COLUMNS = [
  {
    columnName: "prev_cleaning_agent_id",
    displayName: "Cleaning Agents",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Cleaning Agents"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 2,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE (A) or LD50",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "ADE"
  },
  {
    columnName: "next_production_id",
    displayName: "Next Production ID (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Next Production ID"
  },
  {
    columnName: "next_product_name",
    displayName: "Next Product Name (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Next Product Name"
  },
  {
    columnName: "ldd_b",
    displayName: "LDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 6,
    description: "LDD (B)"
  },
  {
    columnName: "mdd_b",
    displayName: "MDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "MDD (B)"
  },
  {
    columnName: "next_unit_weight",
    displayName: "Unit Size (B)",
    dataType: "string",
    isVisible: false,
    isSortable: true,
    isSearchable: false,
    displayOrder: 8,
    description: "Unit Size (B)"
  },
  {
    columnName: "min_bs_b",
    displayName: "Batch Size (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 9,
    description: "Batch Size"
  },
  {
    columnName: "shared_surface_area",
    displayName: "SSA (A-B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 10,
    description: "Shared Surface Area"
  },
  {
    columnName: "general_l3",
    displayName: "SAL-Default",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 11,
    description: "SAL Default"
  },
  {
    columnName: "toxicity_l3",
    displayName: "SAL-Toxicity",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 12,
    description: "SAL Dosage"
  },
  {
    columnName: "cap_l3",
    displayName: "SAL CAP",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 13,
    description: "SAL CAP"
  },
  {
    columnName: "matrix_alert_limit_l3",
    displayName: "SAL-Final",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 14,
    description: "SAL Dosage"
  },
  {
    columnName: "adj_alert_limit_l3",
    displayName: "SAL-Adjusted",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 15,
    description: "SAL Adjusted"
  }
];

export const CLEANING_AGENT_EQUIPMENT_LIMIT_REPORT_COLUMNS = [
  {
    columnName: "prev_cleaning_agent_id",
    displayName: "Cleaning Agent Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Cleaning Agent Id"
  },
  {
    columnName: "prev_cleaning_agent_name",
    displayName: "Cleaning Agent Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Cleaning Agent Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE or LD50",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "ADE"
  },
  {
    columnName: "equipment_id",
    displayName: "Equipment Id",
    dataType: "string",
    isVisible: false,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Equipment Id"
  },
  {
    columnName: "equipment_name",
    displayName: "Equipment Name",
    dataType: "string",
    isVisible: false,
    isSortable: false,
    isSearchable: true,
    displayOrder: 6,
    description: "Equipment Name"
  },
  {
    columnName: "equipment_entity_id",
    displayName: "Equipment Entity Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 7,
    description: "Equipment Entity Id"
  },
  {
    columnName: "equipment_entity_name",
    displayName: "Equipment Entity Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 8,
    description: "Equipment Entity Name"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 9,
    description: "Least SAL"
  }
];

export const CLEANING_AGENT_PRODUCT_REPORT_COLUMNS = [
  {
    columnName: "prev_cleaning_agent_id",
    displayName: "Cleaning Agent Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Cleaning Agent Id"
  },
  {
    columnName: "prev_cleaning_agent_name",
    displayName: "Cleaning Agent Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Cleaning Agent Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE / LD50",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "ADE"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Least SAL"
  }
];

export const MASTER_DATA_LDD_REPORT_COLUMNS = [
  {
    columnName: "production_id",
    displayName: "Production ID",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Production ID"
  },
  {
    columnName: "product_name",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Product Name"
  },
  {
    columnName: "api_name",
    displayName: "API Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "API Name"
  },
  {
    columnName: "ldd_value",
    displayName: "Largest Daily Dose",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "Largest Daily Dose"
  },
  {
    columnName: "min_bs_value",
    displayName: "Batch Size",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Batch Size"
  },
  {
    columnName: "bs_ldd_ratio",
    displayName: "BS/LDD Ratio",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 6,
    description: "BS/LDD Ratio"
  }
];

export const RESIDUE_TRACKER_REPORT_COLUMNS = [
  {
    columnName: "studyId",
    displayName: "Study ID",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: true,
    displayOrder: 1,
    description: "Study ID"
  },
  {
    columnName: "equipmentEntityId",
    displayName: "Equipment Entity ID",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 2,
    description: "Equipment entity identifier"
  },
  {
    columnName: "equipmentEntityName",
    displayName: "Equipment Entity Name",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 3,
    description: "Equipment entity Name"
  },
  {
    columnName: "cleaningProcedureName",
    displayName: "Cleaning Procedure",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 4,
    description: "Cleaning procedure used"
  },
  {
    columnName: "criteriaName",
    displayName: "Criteria",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 5,
    description: "Evaluation criteria"
  },
  {
    columnName: "apiName",
    displayName: "API",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 6,
    description: "API information"
  },
  {
    columnName: "productId",
    displayName: "Product ID",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 7,
    description: "Product identifier"
  },
  {
    columnName: "productName",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 8,
    description: "Name of the product"
  },
  {
    columnName: "fromSource",
    displayName: "From",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 9,
    description: "Source of the report"
  },
  {
    columnName: "approvalDate",
    displayName: "Approval Date",
    dataType: "date",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    navigable: false,
    displayOrder: 10,
    description: "Date of approval"
  },
  {
    columnName: "maxSarValue",
    displayName: "Max SAR",
    dataType: "number",
    isVisible: false,
    isSortable: true,
    isSearchable: false,
    navigable: false,
    displayOrder: 11,
    description: "Maximum SAR value"
  },
  {
    columnName: "productionId",
    displayName: "Production ID",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 12,
    description: "Production External ID"
  },
  {
    columnName: "maxSarValueInCurrentUnit",
    displayName: "Max SAR",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    navigable: false,
    displayOrder: 13,
    description: "Sar value in current facility unit"
  },
  {
    columnName: "navigationUrl",
    displayName: "navigation Url",
    dataType: "string",
    isVisible: false,
    isSortable: false,
    isSearchable: false,
    navigable: false,
    displayOrder: 14,
    description: "navigation Url"
  }
];

export const VERIFICATION_DUE_ON_COLUMN = {
  columnName: "verificationDueOn",
  displayName: "Verification Due On",
  dataType: "string",
  isVisible: true,
  isSortable: false,
  isSearchable: false,
  navigable: false,
  displayOrder: 16,
  description: "Verification due date based on approval date and verification period"
};

export const VALIDATION_TRACKER_REPORT_COLUMNS = [
  {
    dataType: "string",
    isVisible: true,
    columnName: "validationStatus",
    isSortable: true,
    description: "Current validation status",
    displayName: "Validation Status",
    displayOrder: 1,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: true,
    columnName: "equipmentEntityId",
    isSortable: true,
    description: "ID of the equipment entity",
    displayName: "Equipment Entity Id",
    displayOrder: 2,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: true,
    columnName: "equipmentEntityName",
    isSortable: true,
    description: "Name of the equipment entity",
    displayName: "Equipment Entity Name",
    displayOrder: 3,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: true,
    columnName: "cleaningProcedureName",
    isSortable: true,
    description: "Name of the cleaning procedure",
    displayName: "Cleaning procedure",
    displayOrder: 4,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: true,
    columnName: "criteriaName",
    isSortable: true,
    description: "Criteria",
    displayName: "Worst-case Criteria",
    displayOrder: 5,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: true,
    columnName: "productionId",
    isSortable: true,
    description: "Worst-case Production ID",
    displayName: "Worst-case Production ID",
    displayOrder: 6,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: true,
    columnName: "productName",
    isSortable: true,
    description: "Worst-case Product Name",
    displayName: "Worst-case Product Name",
    displayOrder: 7,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: true,
    columnName: "materialName",
    isSortable: true,
    description: "Name of the worst-case material",
    displayName: "Worst-case Material Name",
    displayOrder: 8,
    isSearchable: true,
    navigable: false
  },
  {
    dataType: "string",
    isVisible: false,
    columnName: "materialType",
    isSortable: true,
    description: "Type of the worst-case material (api/intermediate/product)",
    displayName: "Worst-case Material Type",
    displayOrder: 9,
    isSearchable: true,
    navigable: false
  },
  {
    columnName: "sal",
    displayName: "Worst-case Production SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    navigable: false,
    displayOrder: 10,
    description: "Sal value in current facility unit"
  },
  {
    columnName: "provisionalRank",
    displayName: "Provisionaly Validated upto (#rank)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 11,
    description: "Provisionaly Validated upto (#rank)"
  },
  {
    columnName: "provisionalProductionId",
    displayName: "Provisional Production ID",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 12,
    description: "Provisional Production Id"
  },
  {
    columnName: "provisionalApi",
    displayName: "Provisional Material Name",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 13,
    description: "Provisonal API"
  },
  {
    columnName: "studyId",
    displayName: "Study ID",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    navigable: true,
    displayOrder: 14,
    description: "Study ID"
  },
  {
    columnName: "maxSarValueInCurrentUnit",
    displayName: "Max SAR",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    navigable: false,
    displayOrder: 15,
    description: "Sar value in current facility unit"
  },
  {
    columnName: "approvalDate",
    displayName: "Approval Date",
    dataType: "date",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    navigable: false,
    displayOrder: 16,
    description: "Date of approval"
  },
  {
    columnName: "verificationDueOn",
    displayName: "Verification Due On",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: false,
    navigable: false,
    displayOrder: 16,
    description: "Verification due date based on approval date and verification period"
  },
  {
    columnName: "studyInternalId",
    displayName: "Study Internal Id",
    dataType: "string",
    isVisible: false,
    isSortable: false,
    isSearchable: false,
    navigable: false,
    displayOrder: 17,
    description: "Study Internal ID"
  },
  {
    columnName: "fromSource",
    displayName: "From",
    dataType: "string",
    isVisible: false,
    isSortable: true,
    isSearchable: true,
    navigable: false,
    displayOrder: 18,
    description: "Source of the report"
  },
  {
    columnName: "navigationUrl",
    displayName: "navigation Url",
    dataType: "string",
    isVisible: false,
    isSortable: false,
    isSearchable: false,
    navigable: false,
    displayOrder: 19,
    description: "navigation Url"
  }
];

// PDF Configuration Constants for Validation Tracker Report
// PDF Section 2 columns - base config with ALL columns
// At runtime: For API facility - hide equipmentDetails, For non-API facility - hide materialType
export const VALIDATION_TRACKER_PDF_SECTION_2_COLUMNS = [
  {
    name: "Validation Status",
    dataIndex: "validationStatus",
    rank: 1,
    isVisible: true,
    width: 80
  },
  {
    name: "Equipment Details",
    dataIndex: "equipmentDetails",
    rank: 2,
    isVisible: true,
    width: 120
  },
  {
    name: "Cleaning Procedure",
    dataIndex: "cleaningProcedureName",
    rank: 3,
    isVisible: true,
    width: 110
  },
  {
    name: "Worst-case Criteria",
    dataIndex: "criteriaName",
    rank: 4,
    isVisible: true,
    width: 100
  },
  {
    name: "Worst-case Production Details",
    dataIndex: "worstCaseProductionDetails",
    rank: 5,
    isVisible: true,
    width: 140
  },
  {
    name: "Worst-case Production Details",
    dataIndex: "worstCaseProductionDetailsAPI",
    rank: 6,
    isVisible: true,
    width: 140
  },
  {
    name: "Provisionally Validated Rank",
    dataIndex: "provisionalRank",
    rank: 7,
    isVisible: true,
    width: 90
  },
  {
    name: "Provisional Production Details",
    dataIndex: "provisionalProductionDetails",
    rank: 8,
    isVisible: true,
    width: 140
  },
  {
    name: "Provisional Production Details",
    dataIndex: "provisionalProductionDetailsAPI",
    rank: 9,
    isVisible: true,
    width: 140
  },
  {
    name: "Verification Due On",
    dataIndex: "verificationDueOn",
    rank: 10,
    isVisible: true,
    width: 90
  }
];

// PDF Section 3 columns - base config with ALL columns
// At runtime: For non-API facility - hide materialType
export const VALIDATION_TRACKER_PDF_SECTION_3_COLUMNS = [
  {
    name: "Equipment Details",
    dataIndex: "equipmentDetails",
    rank: 1,
    isVisible: true,
    width: 150
  },
  {
    name: "Production Details",
    dataIndex: "productionDetails",
    rank: 2,
    isVisible: true,
    width: 150
  },
  {
    name: "Production Details",
    dataIndex: "productionDetailsAPI",
    rank: 3,
    isVisible: true,
    width: 150
  },
  {
    name: "Provisional Production Details",
    dataIndex: "provisionalProductionDetailsS3",
    rank: 4,
    isVisible: false,
    width: 150
  },
  {
    name: "Provisional Production Details",
    dataIndex: "provisionalProductionDetailsAPIS3",
    rank: 5,
    isVisible: false,
    width: 150
  },
  {
    name: "SAL",
    dataIndex: "sal",
    rank: 6,
    isVisible: true,
    width: 80
  },
  {
    name: "SAR",
    dataIndex: "maxSarValueInCurrentUnit",
    rank: 7,
    isVisible: true,
    width: 80
  },
  {
    name: "Study ID",
    dataIndex: "studyId",
    rank: 8,
    isVisible: true,
    width: 100
  },
  {
    name: "Approval Date",
    dataIndex: "approvalDate",
    rank: 9,
    isVisible: true,
    width: 100
  },
  {
    name: "Worst-case Rank",
    dataIndex: "provisionalRank",
    rank: 10,
    isVisible: true,
    width: 100
  }
];

// Excel Configuration Constants for Validation Tracker Report
export const VALIDATION_TRACKER_EXCEL_COLUMNS = [
  {
    name: "Validation Status",
    dataIndex: "validationStatus",
    rank: 1,
    isVisible: true
  },
  {
    name: "Equipment Entity ID",
    dataIndex: "equipmentEntityId",
    rank: 2,
    isVisible: true
  },
  {
    name: "Equipment Entity Name",
    dataIndex: "equipmentEntityName",
    rank: 3,
    isVisible: true
  },
  {
    name: "Cleaning Procedure",
    dataIndex: "cleaningProcedureName",
    rank: 4,
    isVisible: true
  },
  {
    name: "Worst-case Criteria",
    dataIndex: "criteriaName",
    rank: 5,
    isVisible: true
  },
  {
    name: "Worst-case Production ID",
    dataIndex: "productionId",
    rank: 6,
    isVisible: true
  },
  {
    name: "Worst-case Product Name",
    dataIndex: "productName",
    rank: 7,
    isVisible: true
  },
  {
    name: "Worst-case Material Name",
    dataIndex: "materialName",
    rank: 8,
    isVisible: true
  },
  {
    name: "Worst-case Production SAL",
    dataIndex: "sal",
    rank: 10,
    isVisible: true
  },
  {
    name: "Provisionally Validation Rank",
    dataIndex: "provisionalRank",
    rank: 11,
    isVisible: true
  },
  {
    name: "Provisional Production ID",
    dataIndex: "provisionalProductionId",
    rank: 12,
    isVisible: true
  },
  {
    name: "Provisional Material Name",
    dataIndex: "provisionalApi",
    rank: 13,
    isVisible: true
  },
  {
    name: "Study ID",
    dataIndex: "studyId",
    rank: 14,
    isVisible: true
  },
  {
    name: "Max SAR",
    dataIndex: "maxSarValueInCurrentUnit",
    rank: 15,
    isVisible: true
  },
  {
    name: "Approval Date",
    dataIndex: "approvalDate",
    rank: 16,
    isVisible: true
  },
  {
    name: "Verification Due On",
    dataIndex: "verificationDueOn",
    rank: 17,
    isVisible: true
  }
];

export const VALIDATION_STATUS_CARD_EXCEL_CONFIG = {
  orientation: "portrait",
  headerColumnName: "Validation Status",
  valueColumnName: "Count"
};

// Field formatting constants for PDF sections
export const VALIDATION_TRACKER_PDF_SECTION_2_FIELD_FORMATTING = [
  {
    targetField: "equipmentDetails",
    template: "{equipmentEntityName} (Id: {equipmentEntityId})",
    sourceFields: ["equipmentEntityName", "equipmentEntityId"],
    displayName: "Equipment Details"
  },
  {
    targetField: "worstCaseProductionDetails",
    template: "{productName} (Id: {productionId}) (API: {materialName})",
    sourceFields: ["productName", "productionId", "materialName"],
    displayName: "Worst-case Production Details"
  },
  {
    targetField: "worstCaseProductionDetailsAPI",
    template: "{materialName} (Id: {productionId})",
    sourceFields: ["materialName", "productionId"],
    displayName: "Worst-case Production Details"
  },
  {
    targetField: "provisionalProductionDetails",
    template: "{provisionalProductionId} (API: {provisionalApi})",
    sourceFields: ["provisionalProductionId", "provisionalApi"],
    displayName: "Provisional Production Details"
  },
  {
    targetField: "provisionalProductionDetailsAPI",
    template: "{provisionalApi} (Id: {provisionalProductionId}) (Type: {materialType})",
    sourceFields: ["provisionalApi", "provisionalProductionId", "materialType"],
    displayName: "Provisional Production Details"
  }
];

export const VALIDATION_TRACKER_PDF_SECTION_3_FIELD_FORMATTING = [
  {
    targetField: "equipmentDetails",
    template: "{equipmentEntityName} (Id: {equipmentEntityId})",
    sourceFields: ["equipmentEntityName", "equipmentEntityId"],
    displayName: "Equipment Details"
  },
  {
    targetField: "productionDetails",
    template: "{productName} (Id: {productionId}) (API: {materialName})",
    sourceFields: ["productName", "productionId", "materialName"],
    displayName: "Production Details"
  },
  {
    targetField: "productionDetailsAPI",
    template: "{materialName} (Id: {productionId})",
    sourceFields: ["materialName", "productionId"],
    displayName: "Production Details"
  },
  {
    targetField: "provisionalProductionDetailsS3",
    template: "{provisionalProductionId} (API: {provisionalApi})",
    sourceFields: ["provisionalProductionId", "provisionalApi"],
    displayName: "Provisional Production Details"
  },
  {
    targetField: "provisionalProductionDetailsAPIS3",
    template: "{provisionalApi} (Id: {provisionalProductionId}) (Type: {materialType})",
    sourceFields: ["provisionalApi", "provisionalProductionId", "materialType"],
    displayName: "Provisional Production Details"
  }
];

export const PRODUCTION_LIMIT_MATRIX_FIELD_FORMATTING = [
  {
    targetField: "next_production_id",
    template: "{next_product_name_b} (ID: {next_production_id})",
    sourceFields: ["next_product_name_b", "next_production_id"],
    displayName: "Next Production ID (B)"
  },
  {
    targetField: "prev_production_id",
    template: "{prev_product_name_a} (ID: {prev_production_id})",
    sourceFields: ["prev_product_name_a", "prev_production_id"],
    displayName: "Previous Production (A)"
  }
];

export const CLEANING_AGENT_LIMIT_MATRIX_FIELD_FORMATTING = [
  {
    targetField: "next_production_id",
    template: "{next_product_name} (ID: {next_production_id})",
    sourceFields: ["next_product_name", "next_production_id"],
    displayName: "Next Production ID (B)"
  }
];

export const LIMIT_MATRIX_COLUMNS_PDF = [
  {
    dataIndex: "prev_production_id",
    name: "Previous Production (A)",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_api_name_a",
    name: "Previous API (A)",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_material_type_a",
    name: "Material Type (A)",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "pde_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "ld50_a",
    name: "LD50 (A)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "min_td_a",
    name: "mDD (A)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "safety_factor",
    name: "Safety Factor",
    isVisible: false,
    rank: 7
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "next_material_type_b",
    name: "Material Type (B)",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 12
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 14
  },
  {
    dataIndex: "general_l3",
    name: "SAL-Default",
    isVisible: true,
    rank: 15
  },
  {
    dataIndex: "dosage_l3",
    name: "SAL-Dosage",
    isVisible: true,
    rank: 16
  },
  {
    dataIndex: "ld50_l3",
    name: "SAL-LD50",
    isVisible: true,
    rank: 17
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 18
  },
  {
    dataIndex: "cap_l3",
    name: "SAL CAP",
    isVisible: true,
    rank: 19
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 20
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 21
  }
];

export const LIMIT_MATRIX_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_production_id",
    name: "Previous Production (A)",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Previous Product Name (A)",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_material_type_a",
    name: "Material Type (A)",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "prev_api_name_a",
    name: "Previous API (A)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "pde_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "ld50_a",
    name: "LD50 (A)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "min_td_a",
    name: "mDD (A)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "safety_factor",
    name: "Safety Factor",
    isVisible: false,
    rank: 8
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "next_material_type_b",
    name: "Material Type (B)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "next_product_name_b",
    name: "Next Product Name (B)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 14
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 15
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 16
  },
  {
    dataIndex: "general_l3",
    name: "SAL-Default",
    isVisible: true,
    rank: 17
  },
  {
    dataIndex: "dosage_l3",
    name: "SAL-Dosage",
    isVisible: true,
    rank: 18
  },
  {
    dataIndex: "ld50_l3",
    name: "SAL-LD50",
    isVisible: true,
    rank: 19
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 20
  },
  {
    dataIndex: "cap_l3",
    name: "SAL CAP",
    isVisible: true,
    rank: 21
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 22
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 23
  }
];

export const PRODUCT_LIMIT_COLUMNS_PDF = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_material_type_a",
    name: "Material Type",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const PRODUCT_LIMIT_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_material_type_a",
    name: "Material Type",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const PRODUCT_EQUIPMENT_LIMIT_COLUMNS_PDF = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "active_contact",
    name: "Active Contact",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "material_contact",
    name: "Material Contact",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "equipment_id",
    name: "Equipment Id",
    isVisible: false,
    rank: 6
  },
  {
    dataIndex: "equipment_name",
    name: "Equipment Name",
    isVisible: false,
    rank: 7
  },
  {
    dataIndex: "equipment_entity_id",
    name: "Equipment Entity Id",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "equipment_entity_name",
    name: "Equipment Entity Name",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "min_alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 10
  }
];

export const PRODUCT_EQUIPMENT_LIMIT_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "active_contact",
    name: "Active Contact",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "material_contact",
    name: "Material Contact",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "equipment_id",
    name: "Equipment Id",
    isVisible: false,
    rank: 6
  },
  {
    dataIndex: "equipment_name",
    name: "Equipment Name",
    isVisible: false,
    rank: 7
  },
  {
    dataIndex: "equipment_entity_id",
    name: "Equipment Entity Id",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "equipment_entity_name",
    name: "Equipment Entity Name",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "min_alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 9
  }
];

export const MACO_COLUMNS_PDF = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production Id",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "next_product_name_b",
    name: "Next Production Name",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA",
    isVisible: true,
    displayOrder: 7
  },
  {
    dataIndex: "maco",
    name: "MACO (mg)",
    isVisible: true,
    rank: 8
  }
];

export const MACO_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production Id",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "next_product_name_b",
    name: "Next Production Name",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA",
    isVisible: true,
    displayOrder: 7
  },
  {
    dataIndex: "maco",
    name: "MACO (mg)",
    isVisible: true,
    rank: 8
  }
];

export const MAXCONC_COLUMNS_PDF = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production Id",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_product_name_b",
    name: "Next Product Name",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "batch_size_mg",
    name: "Batch Size (mg)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "batch_size_gm",
    name: "Batch Size (gm)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "batch_size_kg",
    name: "Batch Size (kg)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (sqcm)",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL (mg/sqcm)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "maco",
    name: "MACO (mg)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "maco_ppm",
    name: "MACO (ppm)",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "maxconc_ppm",
    name: "MAXCONC (ppm)",
    isVisible: true,
    rank: 13
  }
];

export const MAXCONC_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_product_name_a",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_api_name_a",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production Id",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_product_name_b",
    name: "Next Product Name",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "batch_size_mg",
    name: "Batch Size (mg)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "batch_size_gm",
    name: "Batch Size (gm)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "batch_size_kg",
    name: "Batch Size (kg)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (sqcm)",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL (mg/sqcm)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "maco",
    name: "MACO (mg)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "maco_ppm",
    name: "MACO (ppm)",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "maxconc_ppm",
    name: "MAXCONC (ppm)",
    isVisible: true,
    rank: 13
  }
];

/**
 * Formula fields for MAXCONC report - computed at runtime using pure SQL expressions
 * Use column names and SQL functions directly (LEAST, GREATEST, POWER, etc.)
 * maco_ppm = maco / batch_size_kg
 * maxconc_ppm = if batch_size_gm > 20 then LEAST(10, maco_ppm) else LEAST(100, maco_ppm)
 */
export const MAXCONC_FORMULA_FIELDS = [
  {
    targetField: "maco_ppm",
    formula: "maco / batch_size_kg",
    sourceFields: ["maco", "batch_size_kg"],
    displayName: "MACO (ppm)",
    outputType: "number" as const,
    precision: 6
  },
  {
    targetField: "maxconc_ppm",
    formula: "CONDITIONAL",
    sourceFields: ["batch_size_gm", "maco", "batch_size_kg"],
    displayName: "MAXCONC (ppm)",
    outputType: "number" as const,
    precision: 6,
    condition: {
      when: "batch_size_gm > 20",
      then: "LEAST(10, maco / batch_size_kg)",
      else: "LEAST(100, maco / batch_size_kg)"
    }
  }
];

export const CLEANING_AGENT_LIMIT_MATRIX_COLUMNS_PDF = [
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agents",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 7
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "general_l3",
    name: "SAL-Default",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "cap_l3",
    name: "SAL CAP",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 14
  }
];

export const CLEANING_AGENT_LIMIT_MATRIX_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agents",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_product_name",
    name: "Next Product Name (B)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 8
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "general_l3",
    name: "SAL-Default",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "cap_l3",
    name: "SAL CAP",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 14
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 15
  }
];

export const CLEANING_AGENT_EQUIPMENT_LIMIT_REPORT_COLUMNS_PDF = [
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "equipment_id",
    name: "Equipment Id",
    isVisible: false,
    rank: 5
  },
  {
    dataIndex: "equipment_name",
    name: "Equipment Name",
    isVisible: false,
    rank: 6
  },
  {
    dataIndex: "equipment_entity_id",
    name: "Equipment Entity Id",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "equipment_entity_name",
    name: "Equipment Entity Name",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 9
  }
];

export const CLEANING_AGENT_EQUIPMENT_LIMIT_REPORT_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "equipment_id",
    name: "Equipment Id",
    isVisible: false,
    rank: 5
  },
  {
    dataIndex: "equipment_name",
    name: "Equipment Name",
    isVisible: false,
    rank: 6
  },
  {
    dataIndex: "equipment_entity_id",
    name: "Equipment Entity Id",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "equipment_entity_name",
    name: "Equipment Entity Name",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 9
  }
];

export const CLEANING_AGENT_PRODUCT_REPORT_COLUMNS_PDF = [
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const CLEANING_AGENT_PRODUCT_REPORT_COLUMNS_EXCEL = [
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_cleaning_agent_id",
    name: "Cleaning Agent Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A)",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const MASTER_DATA_LDD_REPORT_COLUMNS_PDF = [
  {
    dataIndex: "production_id",
    name: "Production ID",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "product_name",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "api_name",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ldd_value",
    name: "Largest Daily Dose",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "min_bs_value",
    name: "Batch Size",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "bs_ldd_ratio",
    name: "BS/LDD Ratio",
    isVisible: true,
    rank: 6
  }
];

export const MASTER_DATA_LDD_REPORT_COLUMNS_EXCEL = [
  {
    dataIndex: "production_id",
    name: "Production ID",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "product_name",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "api_name",
    name: "API Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ldd_value",
    name: "Largest Daily Dose",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "min_bs_value",
    name: "Batch Size",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "bs_ldd_ratio",
    name: "BS/LDD Ratio",
    isVisible: true,
    rank: 6
  }
];

// ===== PRODUCT RANKING REPORT CONSTANTS =====
export const PRODUCT_RANKING_REPORT_COLUMNS = [
  {
    columnName: "api_name",
    displayName: "API Name",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: true,
    displayOrder: 1,
    description: "API Name"
  },
  {
    columnName: "daily_dose_mg",
    displayName: "Daily Dose (mg)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 2,
    description: "Daily Dose in mg"
  },
  {
    columnName: "ade_mg",
    displayName: "ADE (mg)",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "ADE Value in mg"
  },
  {
    columnName: "recovery_by_hplc",
    displayName: "Recovery Value By HPLC",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "Recovery Value By HPLC"
  },
  {
    columnName: "recovery_index",
    displayName: "Recovery Index By HPLC",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Index based on Recovery By HPLC"
  },
  {
    columnName: "potency_index_tdd",
    displayName: "Potency Index by TDD",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 6,
    description: "Potency Index based on TDD"
  },
  {
    columnName: "worst_case_tdd",
    displayName: "Worst Case by TDD",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "Recovery Index × Potency Index by TDD"
  },
  {
    columnName: "potency_index_ade",
    displayName: "Potency Index by ADE",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 8,
    description: "Potency Index based on ADE"
  },
  {
    columnName: "worst_case_ade",
    displayName: "Worst Case by ADE",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 9,
    description: "Recovery Index × Potency Index by ADE"
  }
];

export const PRODUCT_RANKING_REPORT_COLUMNS_PDF = [
  { dataIndex: "api_name", name: "API Name", isVisible: true, rank: 1 },
  { dataIndex: "daily_dose_mg", name: "Daily Dose (mg)", isVisible: true, rank: 2 },
  { dataIndex: "ade_mg", name: "ADE (mg)", isVisible: true, rank: 3 },
  { dataIndex: "recovery_by_hplc", name: "Recovery Value By HPLC", isVisible: true, rank: 4 },
  { dataIndex: "recovery_index", name: "Recovery Index By HPLC", isVisible: true, rank: 5 },
  { dataIndex: "potency_index_tdd", name: "Potency Index by TDD", isVisible: true, rank: 6 },
  { dataIndex: "worst_case_tdd", name: "Worst Case by TDD", isVisible: true, rank: 7 },
  { dataIndex: "potency_index_ade", name: "Potency Index by ADE", isVisible: true, rank: 8 },
  { dataIndex: "worst_case_ade", name: "Worst Case by ADE", isVisible: true, rank: 9 }
];

export const PRODUCT_RANKING_REPORT_COLUMNS_EXCEL = [
  { dataIndex: "api_name", name: "API Name", isVisible: true, rank: 1 },
  { dataIndex: "daily_dose_mg", name: "Daily Dose (mg)", isVisible: true, rank: 2 },
  { dataIndex: "ade_mg", name: "ADE (mg)", isVisible: true, rank: 3 },
  { dataIndex: "recovery_by_hplc", name: "Recovery Value By HPLC", isVisible: true, rank: 4 },
  { dataIndex: "recovery_index", name: "Recovery Index By HPLC", isVisible: true, rank: 5 },
  { dataIndex: "potency_index_tdd", name: "Potency Index by TDD", isVisible: true, rank: 6 },
  { dataIndex: "worst_case_tdd", name: "Worst Case by TDD", isVisible: true, rank: 7 },
  { dataIndex: "potency_index_ade", name: "Potency Index by ADE", isVisible: true, rank: 8 },
  { dataIndex: "worst_case_ade", name: "Worst Case by ADE", isVisible: true, rank: 9 }
];

// Formula fields for Product Ranking Report (calculated after base data insert)
export const PRODUCT_RANKING_FORMULA_FIELDS = [
  {
    targetField: "worst_case_tdd",
    formula: "recovery_index * potency_index_tdd",
    sourceFields: ["recovery_index", "potency_index_tdd"],
    displayName: "Worst Case by TDD",
    outputType: "number" as const
  },
  {
    targetField: "worst_case_ade",
    formula: "recovery_index * potency_index_ade",
    sourceFields: ["recovery_index", "potency_index_ade"],
    displayName: "Worst Case by ADE",
    outputType: "number" as const
  }
];

// Required additional field key for Product Ranking Report
export const PRODUCT_RANKING_REQUIRED_ADDITIONAL_FIELD_KEY = "recoveryValueByHPLC";

// ===== NITROSAMINE CONSTANTS =====
export const NITROSAMINE_LIMIT_REPORT_COLUMNS = [
  {
    columnName: "prev_nitrosamine_id",
    displayName: "Nitrosamine Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Nitrosamine Id"
  },
  {
    columnName: "prev_nitrosamine_name",
    displayName: "Nitrosamine Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Nitrosamine Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE or AI",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "ADE or AI"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Least SAL"
  }
];

export const NITROSAMINE_LIMIT_REPORT_PDF_COLUMNS = [
  {
    dataIndex: "prev_nitrosamine_id",
    name: "Nitrosamine Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_nitrosamine_name",
    name: "Nitrosamine Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const NITROSAMINE_LIMIT_REPORT_EXCEL_COLUMNS = [
  {
    dataIndex: "prev_nitrosamine_id",
    name: "Nitrosamine Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_nitrosamine_name",
    name: "Nitrosamine Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const NITROSAMINE_LIMIT_MATRIX_COLUMNS = [
  {
    columnName: "prev_nitrosamine_id",
    displayName: "Nitrosamine Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Nitrosamine Id"
  },
  {
    columnName: "prev_nitrosamine_name",
    displayName: "Nitrosamine Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Nitrosamine Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE (A) or AI",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "ADE"
  },
  {
    columnName: "next_production_id",
    displayName: "Next Production ID (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Next Production ID"
  },
  {
    columnName: "next_product_name",
    displayName: "Next Product Name (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 6,
    description: "Next Product Name"
  },
  {
    columnName: "ldd_b",
    displayName: "LDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "LDD (B)"
  },
  {
    columnName: "mdd_b",
    displayName: "MDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 8,
    description: "MDD (B)"
  },
  {
    columnName: "next_unit_weight",
    displayName: "Unit Size (B)",
    dataType: "string",
    isVisible: false,
    isSortable: true,
    isSearchable: false,
    displayOrder: 9,
    description: "Unit Size (B)"
  },
  {
    columnName: "min_bs_b",
    displayName: "Batch Size (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 10,
    description: "Batch Size"
  },
  {
    columnName: "shared_surface_area",
    displayName: "SSA (A-B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 11,
    description: "Shared Surface Area"
  },
  {
    columnName: "toxicity_l3",
    displayName: "SAL-Toxicity",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 12,
    description: "SAL Toxicity"
  },
  {
    columnName: "cap_l3",
    displayName: "SAL-Cap",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 13,
    description: "SAL Cap"
  },
  {
    columnName: "matrix_alert_limit_l3",
    displayName: "SAL-Final",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 14,
    description: "SAL Final"
  },
  {
    columnName: "adj_alert_limit_l3",
    displayName: "SAL-Adjusted",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 15,
    description: "SAL Adjusted"
  }
];

export const NITROSAMINE_LIMIT_MATRIX_PDF_COLUMNS = [
  {
    dataIndex: "prev_nitrosamine_id",
    name: "Nitrosamine Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_nitrosamine_name",
    name: "Nitrosamine Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A) or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "next_product_name",
    name: "Next Product Name (B)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 9
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "cap_l3",
    name: "SAL-Cap",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 14
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 15
  }
];

export const NITROSAMINE_LIMIT_MATRIX_EXCEL_COLUMNS = [
  {
    dataIndex: "prev_nitrosamine_id",
    name: "Nitrosamine Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_nitrosamine_name",
    name: "Nitrosamine Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A) or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "next_product_name",
    name: "Next Product Name (B)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 9
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "cap_l3",
    name: "SAL-Cap",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 14
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 15
  }
];

export const NITROSAMINE_PRODUCTION_LIMIT_COLUMNS = [
  {
    columnName: "next_production_id",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Production Id"
  },
  {
    columnName: "next_product_name",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Product Name"
  },
  {
    columnName: "prev_nitrosamine_id",
    displayName: "Nitrosamine Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "Nitrosamine Id"
  },
  {
    columnName: "prev_nitrosamine_name",
    displayName: "Nitrosamine Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Nitrosamine Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE or AI",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 6,
    description: "ADE or AI"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "Least SAL"
  }
];

export const NITROSAMINE_PRODUCTION_LIMIT_PDF_COLUMNS = [
  {
    dataIndex: "next_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "next_product_name",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_nitrosamine_id",
    name: "Nitrosamine Id",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "prev_nitrosamine_name",
    name: "Nitrosamine Name",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 7
  }
];

export const NITROSAMINE_PRODUCTION_LIMIT_EXCEL_COLUMNS = [
  {
    dataIndex: "next_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "next_product_name",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_nitrosamine_id",
    name: "Nitrosamine Id",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "prev_nitrosamine_name",
    name: "Nitrosamine Name",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 7
  }
];

// ===== GENOTOXIC CONSTANTS =====
export const GENOTOXIC_LIMIT_REPORT_COLUMNS = [
  {
    columnName: "prev_genotoxic_id",
    displayName: "Genotoxic Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Genotoxic Id"
  },
  {
    columnName: "prev_genotoxic_name",
    displayName: "Genotoxic Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Genotoxic Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE or AI",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "ADE or AI"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Least SAL"
  }
];

export const GENOTOXIC_LIMIT_REPORT_PDF_COLUMNS = [
  {
    dataIndex: "prev_genotoxic_id",
    name: "Genotoxic Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_genotoxic_name",
    name: "Genotoxic Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const GENOTOXIC_LIMIT_REPORT_EXCEL_COLUMNS = [
  {
    dataIndex: "prev_genotoxic_id",
    name: "Genotoxic Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_genotoxic_name",
    name: "Genotoxic Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 5
  }
];

export const GENOTOXIC_LIMIT_MATRIX_COLUMNS = [
  {
    columnName: "prev_genotoxic_id",
    displayName: "Genotoxic Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Genotoxic Id"
  },
  {
    columnName: "prev_genotoxic_name",
    displayName: "Genotoxic Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Genotoxic Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 3,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE (A) or AI",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 4,
    description: "ADE"
  },
  {
    columnName: "next_production_id",
    displayName: "Next Production ID (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Next Production ID"
  },
  {
    columnName: "next_product_name",
    displayName: "Next Product Name (B)",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 6,
    description: "Next Product Name"
  },
  {
    columnName: "ldd_b",
    displayName: "LDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "LDD (B)"
  },
  {
    columnName: "mdd_b",
    displayName: "MDD (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 8,
    description: "MDD (B)"
  },
  {
    columnName: "next_unit_weight",
    displayName: "Unit Size (B)",
    dataType: "string",
    isVisible: false,
    isSortable: true,
    isSearchable: false,
    displayOrder: 9,
    description: "Unit Size (B)"
  },
  {
    columnName: "min_bs_b",
    displayName: "Batch Size (B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 10,
    description: "Batch Size"
  },
  {
    columnName: "shared_surface_area",
    displayName: "SSA (A-B)",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 11,
    description: "Shared Surface Area"
  },
  {
    columnName: "toxicity_l3",
    displayName: "SAL-Toxicity",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 12,
    description: "SAL Toxicity"
  },
  {
    columnName: "cap_l3",
    displayName: "SAL-Cap",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 13,
    description: "SAL Cap"
  },
  {
    columnName: "matrix_alert_limit_l3",
    displayName: "SAL-Final",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 14,
    description: "SAL Final"
  },
  {
    columnName: "adj_alert_limit_l3",
    displayName: "SAL-Adjusted",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 15,
    description: "SAL Adjusted"
  }
];

export const GENOTOXIC_LIMIT_MATRIX_PDF_COLUMNS = [
  {
    dataIndex: "prev_genotoxic_id",
    name: "Genotoxic Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_genotoxic_name",
    name: "Genotoxic Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A) or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "next_product_name",
    name: "Next Product Name (B)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 9
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "cap_l3",
    name: "SAL-Cap",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 14
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 15
  }
];

export const GENOTOXIC_LIMIT_MATRIX_EXCEL_COLUMNS = [
  {
    dataIndex: "prev_genotoxic_id",
    name: "Genotoxic Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "prev_genotoxic_name",
    name: "Genotoxic Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "ade_a",
    name: "ADE (A) or AI",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "next_production_id",
    name: "Next Production ID (B)",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "next_product_name",
    name: "Next Product Name (B)",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "ldd_b",
    name: "LDD (B)",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "mdd_b",
    name: "MDD (B)",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "next_unit_weight",
    name: "Unit Size (B)",
    isVisible: false,
    rank: 9
  },
  {
    dataIndex: "min_bs_b",
    name: "Batch Size (B)",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "shared_surface_area",
    name: "SSA (A-B)",
    isVisible: true,
    rank: 11
  },
  {
    dataIndex: "toxicity_l3",
    name: "SAL-Toxicity",
    isVisible: true,
    rank: 12
  },
  {
    dataIndex: "cap_l3",
    name: "SAL-Cap",
    isVisible: true,
    rank: 13
  },
  {
    dataIndex: "matrix_alert_limit_l3",
    name: "SAL-Final",
    isVisible: true,
    rank: 14
  },
  {
    dataIndex: "adj_alert_limit_l3",
    name: "SAL-Adjusted",
    isVisible: true,
    rank: 15
  }
];

export const GENOTOXIC_PRODUCTION_LIMIT_COLUMNS = [
  {
    columnName: "next_production_id",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 1,
    description: "Production Id"
  },
  {
    columnName: "next_product_name",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Product Name"
  },
  {
    columnName: "prev_genotoxic_id",
    displayName: "Genotoxic Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "Genotoxic Id"
  },
  {
    columnName: "prev_genotoxic_name",
    displayName: "Genotoxic Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Genotoxic Name"
  },
  {
    columnName: "configuration",
    displayName: "Configured Toxicology as",
    dataType: "string",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 5,
    description: "Configured Toxicology as"
  },
  {
    columnName: "ade_a",
    displayName: "ADE or AI",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 6,
    description: "ADE or AI"
  },
  {
    columnName: "alert_limit_l3",
    displayName: "Least SAL",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 7,
    description: "Least SAL"
  }
];

export const GENOTOXIC_PRODUCTION_LIMIT_PDF_COLUMNS = [
  {
    dataIndex: "next_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "next_product_name",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_genotoxic_id",
    name: "Genotoxic Id",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "prev_genotoxic_name",
    name: "Genotoxic Name",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 7
  }
];

export const GENOTOXIC_PRODUCTION_LIMIT_EXCEL_COLUMNS = [
  {
    dataIndex: "next_production_id",
    name: "Production Id",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "next_product_name",
    name: "Product Name",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "prev_genotoxic_id",
    name: "Genotoxic Id",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "prev_genotoxic_name",
    name: "Genotoxic Name",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "configuration",
    name: "Configured Toxicology as",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "ade_a",
    name: "ADE or AI",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "alert_limit_l3",
    name: "Least SAL",
    isVisible: true,
    rank: 7
  }
];

// ===== WORST CASE RANKING CONSTANTS =====
// Static columns for worst case ranking - rule columns will be added dynamically during assessment

// 1. Equipment-wise UI columns (static columns without dynamic rule columns)
export const WORST_CASE_RANKING_EQ_WISE_UI_COLUMNS = [
  {
    columnName: "localRank",
    displayName: "Rank - Equipment Level",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 1,
    description: "Local Rank"
  },
  {
    columnName: "equipmentEntityId",
    displayName: "Equipment Entity Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Equipment Entity ID"
  },
  {
    columnName: "equipmentEntityName",
    displayName: "Equipment Entity Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "Equipment Entity Name"
  },
  {
    columnName: "equipmentDetails",
    displayName: "Equipment Details",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Equipment Details"
  },
  {
    columnName: "cleaningProcedure",
    displayName: "Cleaning Procedure",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Cleaning Procedure"
  },
  {
    columnName: "productionId",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 6,
    description: "Production ID"
  },
  {
    columnName: "productName",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 7,
    description: "Product Name"
  },
  {
    columnName: "materialType",
    displayName: "Material Type",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 8,
    description: "Material Type"
  },
  {
    columnName: "materialName",
    displayName: "Material Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 9,
    description: "Material Name"
  },
  {
    columnName: "impuritiesId",
    displayName: "Impurity Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 10,
    description: "Impurities ID"
  },
  {
    columnName: "impurityName",
    displayName: "Impurity Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 11,
    description: "Impurity Name"
  }
];

// 2. Facility-wise UI columns (static columns without dynamic rule columns)
export const WORST_CASE_RANKING_FACILITY_WISE_UI_COLUMNS = [
  {
    columnName: "globalRank",
    displayName: "Rank - Facility Level",
    dataType: "number",
    isVisible: true,
    isSortable: true,
    isSearchable: false,
    displayOrder: 1,
    description: "Global Rank"
  },
  {
    columnName: "productionId",
    displayName: "Production Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 2,
    description: "Production ID"
  },
  {
    columnName: "productName",
    displayName: "Product Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 3,
    description: "Product Name"
  },
  {
    columnName: "materialType",
    displayName: "Material Type",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 4,
    description: "Material Type"
  },
  {
    columnName: "materialName",
    displayName: "Material Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 5,
    description: "Material Name"
  },
  {
    columnName: "impuritiesId",
    displayName: "Impurity Id",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 6,
    description: "Impurities ID"
  },
  {
    columnName: "impurityName",
    displayName: "Impurity Name",
    dataType: "string",
    isVisible: true,
    isSortable: false,
    isSearchable: true,
    displayOrder: 7,
    description: "Impurity Name"
  }
];

// 3. Equipment-wise PDF columns (static columns without dynamic rule columns)
export const WORST_CASE_RANKING_EQ_WISE_PDF_COLUMNS = [
  {
    dataIndex: "localRank",
    name: "Rank - Equipment Level",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "equipmentEntityId",
    name: "Equipment Entity Id",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "equipmentEntityName",
    name: "Equipment Entity Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "equipmentDetails",
    name: "Equipment Details",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "cleaningProcedure",
    name: "Cleaning Procedure",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "productionId",
    name: "Production Id",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "productName",
    name: "Product Name",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "materialType",
    name: "Material Type",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "materialName",
    name: "Material Name",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "impuritiesId",
    name: "Impurity Id",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "impurityName",
    name: "Impurity Name",
    isVisible: true,
    rank: 11
  }
];

// 4. Facility-wise PDF columns (static columns without dynamic rule columns)
export const WORST_CASE_RANKING_FACILITY_WISE_PDF_COLUMNS = [
  {
    dataIndex: "globalRank",
    name: "Rank - Facility Level",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "productionId",
    name: "Prod Id",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "productName",
    name: "Product Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "materialType",
    name: "Material Type",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "materialName",
    name: "Material Name",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "impuritiesId",
    name: "Impurity Id",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "impurityName",
    name: "Impurity Name",
    isVisible: true,
    rank: 7
  }
];

// 5. Equipment-wise Excel columns (static columns without dynamic rule columns)
export const WORST_CASE_RANKING_EQ_WISE_EXCEL_COLUMNS = [
  {
    dataIndex: "localRank",
    name: "Rank - Equipment Level",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "equipmentEntityId",
    name: "Equipment Entity Id",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "equipmentEntityName",
    name: "Equipment Entity Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "equipmentDetails",
    name: "Equipment Details",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "cleaningProcedure",
    name: "Cleaning Procedure",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "productionId",
    name: "Production Id",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "productName",
    name: "Product Name",
    isVisible: true,
    rank: 7
  },
  {
    dataIndex: "materialType",
    name: "Material Type",
    isVisible: true,
    rank: 8
  },
  {
    dataIndex: "materialName",
    name: "Material Name",
    isVisible: true,
    rank: 9
  },
  {
    dataIndex: "impuritiesId",
    name: "Impurity Id",
    isVisible: true,
    rank: 10
  },
  {
    dataIndex: "impurityName",
    name: "Impurity Name",
    isVisible: true,
    rank: 11
  }
];

// 6. Facility-wise Excel columns (static columns without dynamic rule columns)
export const WORST_CASE_RANKING_FACILITY_WISE_EXCEL_COLUMNS = [
  {
    dataIndex: "globalRank",
    name: "Rank - Facility Level",
    isVisible: true,
    rank: 1
  },
  {
    dataIndex: "productionId",
    name: "Production Id",
    isVisible: true,
    rank: 2
  },
  {
    dataIndex: "productName",
    name: "Product Name",
    isVisible: true,
    rank: 3
  },
  {
    dataIndex: "materialType",
    name: "Material Type",
    isVisible: true,
    rank: 4
  },
  {
    dataIndex: "materialName",
    name: "Material Name",
    isVisible: true,
    rank: 5
  },
  {
    dataIndex: "impuritiesId",
    name: "Impurity Id",
    isVisible: true,
    rank: 6
  },
  {
    dataIndex: "impurityName",
    name: "Impurity Name",
    isVisible: true,
    rank: 7
  }
];
